import {
  DepositBehavior,
  FlashDesignPricings,
  FlashDesignSize,
  Flashbooks,
  FlashDays,
  FlashDesigns,
  FlashDesignsToEmployees,
  UploadedImages,
  Businesses,
} from '@prisma/client';
import * as yup from 'yup';

export interface FlashbookDesign extends FlashDesigns {
  images: UploadedImages[];
  flashbook?: Pick<Flashbooks, 'businessId'> & {
    business: Pick<Businesses, 'urlPath'> | null;
  };
}

export interface FlashbookWithRelations extends Flashbooks {
  flashDays: FlashDays[];
}

export interface BusinessFlashDay {
  id: number;
  date: string;
  name: string;
  timezone: string;
}

export interface FlashDesignWithRelations extends FlashDesigns {
  images: UploadedImages[];
  pricing: FlashDesignPricings[];
  assignedEmployees: Pick<FlashDesignsToEmployees, 'employeeId'>[];
}

export interface SizeConfig {
  size: FlashDesignSize;
  enabled: boolean;
  durationInMinutes: number | null;
  estimate: string;
}

const parseAmount = (value: string | null | undefined) => {
  if (!value) return 0;
  return parseFloat(value.replace(/[^0-9.]/g, '') || '0');
};

export const sizeConfigSchema = yup.object({
  size: yup
    .mixed<FlashDesignSize>()
    .oneOf(['SMALL', 'MEDIUM', 'LARGE', 'EXTRA_LARGE'])
    .required(),
  enabled: yup.boolean().required(),
  durationInMinutes: yup
    .number()
    .nullable()
    .when('enabled', {
      is: true,
      then: schema =>
        schema
          .nonNullable()
          .required('Duration is required')
          .min(1, 'Duration must be greater than 0'),
      otherwise: schema => schema.nullable().notRequired(),
    }),
  estimate: yup.string().when('enabled', {
    is: true,
    then: schema =>
      schema
        .required('Estimate is required')
        .test(
          'is-valid-amount',
          'Estimate must be greater than 0',
          value => parseAmount(value) > 0,
        ),
    otherwise: schema => schema.nullable(),
  }),
});

const depositBehaviorSchema = yup
  .mixed<DepositBehavior>()
  .oneOf(['NONE', 'FLAT_FEE', 'PERCENTAGE', 'FULL'])
  .required('Payment rule is required');

const depositAmountSchema = yup.string().when('depositBehavior', {
  is: 'FLAT_FEE',
  then: schema =>
    schema
      .required('Deposit amount is required')
      .test(
        'is-valid-amount',
        'Deposit amount must be greater than 0',
        value => parseAmount(value) > 0,
      ),
  otherwise: schema => schema.nullable().notRequired(),
});

const depositPercentageSchema = yup.string().when('depositBehavior', {
  is: 'PERCENTAGE',
  then: schema =>
    schema
      .required('Deposit percentage is required')
      .test(
        'is-valid-percentage',
        'Deposit percentage must be between 1 and 100',
        value => {
          const numValue = parseAmount(value);
          return numValue >= 1 && numValue <= 100;
        },
      ),
  otherwise: schema => schema.nullable().notRequired(),
});

export const flashDesignFormSchema = yup.object({
  name: yup.string().trim().required('Title is required'),
  description: yup.string().nullable().notRequired(),
  imageUrl: yup.string().required('A flash design image is required'),
  tags: yup.array().of(yup.string().required()).required(),
  isRepeatable: yup.boolean().required(),
  enableDirectBookings: yup.boolean().required(),
  depositBehavior: depositBehaviorSchema,
  depositAmount: depositAmountSchema,
  depositPercentage: depositPercentageSchema,
  sizeConfigs: yup
    .array()
    .of(sizeConfigSchema)
    .required()
    .when('enableDirectBookings', {
      is: true,
      then: schema =>
        schema.test(
          'at-least-one-enabled',
          'At least one size must be enabled when direct bookings are on',
          value => Array.isArray(value) && value.some(config => config.enabled),
        ),
      otherwise: schema => schema,
    }),
  bookableArtistIds: yup.array().of(yup.string().required()).required(),
});

export type FlashDesignFormPayload = yup.InferType<
  typeof flashDesignFormSchema
>;

export const flashbookSettingsSchema = yup.object({
  enabled: yup.boolean().required(),
  enableDirectBookings: yup.boolean().required(),
  depositBehavior: depositBehaviorSchema,
  depositAmount: depositAmountSchema,
  depositPercentage: depositPercentageSchema,
  applyToAllDesigns: yup.boolean().required(),
});

export type FlashbookSettingsPayload = yup.InferType<
  typeof flashbookSettingsSchema
>;

export interface FlashDesignPricingPayload {
  enabled: boolean;
  size: FlashDesignSize;
  price: number;
  estimatedDurationInMinutes: number;
}

export interface CreateFlashDesignPayload {
  name: string;
  description?: string;
  tags?: string[];
  isRepeatable: boolean;
  enableDirectBookings: boolean;
  depositBehavior: DepositBehavior;
  depositAmount: number;
  depositPercentage: number;
  images: string[];
  pricing?: FlashDesignPricingPayload[];
  allBookableArtists?: boolean;
  assignedEmployeeIds?: string[];
}

export interface UpdateFlashDesignPayload {
  name?: string;
  description?: string;
  tags?: string[];
  isRepeatable?: boolean;
  enableDirectBookings?: boolean;
  depositBehavior?: DepositBehavior;
  depositAmount?: number;
  depositPercentage?: number;
  images?: string[];
  pricing?: FlashDesignPricingPayload[];
  archived?: boolean;
  allBookableArtists?: boolean;
  assignedEmployeeIds?: string[];
}

export interface UpdateFlashbookPayload {
  enabled?: boolean;
  enableDirectBookings?: boolean;
  depositBehavior?: DepositBehavior;
  depositAmount?: number;
  depositPercentage?: number;
  applyToAllDesigns?: boolean;
}

export interface UpdateDesignsSortOrderPayload {
  sortedDesignIds: number[];
}

export interface FlashDesignsListMeta {
  cursor: number | undefined;
  hasMore: boolean;
}
